import { BitkitTourOptions, useBitkitTour } from '@bitrise/bitkit-v2';
import { useEffect, useId, useState } from 'react';
import { create } from 'zustand';

export type TourScope = 'page' | 'global';

type TourStatus = 'idle' | 'pending' | 'wanting';

type RegisteredTour = {
  scope: TourScope;
  status: TourStatus;
};

type TourGate = {
  tours: Record<string, RegisteredTour>;
  requestOrder: string[];
  shownTourId: string | null;
  register: (tourId: string, scope: TourScope) => void;
  unregister: (tourId: string) => void;
  setStatus: (tourId: string, status: TourStatus) => void;
  markShown: (tourId: string) => void;
};

const useTourGate = create<TourGate>((set) => ({
  tours: {},
  requestOrder: [],
  shownTourId: null,
  register: (tourId, scope) => set((state) => ({ tours: { ...state.tours, [tourId]: { scope, status: 'idle' } } })),
  unregister: (tourId) =>
    set((state) => {
      const { [tourId]: _, ...tours } = state.tours;
      return {
        tours,
        requestOrder: state.requestOrder.filter((id) => id !== tourId),
        shownTourId: state.shownTourId === tourId ? null : state.shownTourId,
      };
    }),
  setStatus: (tourId, status) =>
    set((state) => {
      const tour = state.tours[tourId];
      if (!tour || tour.status === status) {
        return state;
      }

      const withoutTour = state.requestOrder.filter((id) => id !== tourId);
      return {
        tours: { ...state.tours, [tourId]: { ...tour, status } },
        requestOrder: status === 'wanting' ? [...withoutTour, tourId] : withoutTour,
      };
    }),
  markShown: (tourId) => set((state) => (state.shownTourId === tourId ? state : { shownTourId: tourId })),
}));

const pickSlotHolder = ({ requestOrder, shownTourId, tours }: TourGate) => {
  if (shownTourId) {
    return tours[shownTourId]?.status === 'wanting' ? shownTourId : null;
  }

  const pageTourWanting = requestOrder.find((id) => tours[id].scope === 'page');
  if (pageTourWanting) {
    return pageTourWanting;
  }

  const isPageTourPending = Object.values(tours).some(({ scope, status }) => scope === 'page' && status === 'pending');
  if (isPageTourPending) {
    return null;
  }

  return requestOrder[0] ?? null;
};

type GatedBitkitTourOptions = BitkitTourOptions & {
  scope: TourScope;
  isOpenPending?: boolean;
};

const useGatedBitkitTour = ({
  isOpenPending = false,
  onClose,
  onStatusChange,
  open = false,
  scope,
  ...options
}: GatedBitkitTourOptions) => {
  const tourId = useId();
  const [hasEnded, setHasEnded] = useState(false);
  const [previousOpen, setPreviousOpen] = useState(open);
  if (open !== previousOpen) {
    setPreviousOpen(open);
    if (open) {
      setHasEnded(false);
    }
  }

  const wantsToOpen = open && !hasEnded;
  const status: TourStatus = wantsToOpen ? 'wanting' : isOpenPending ? 'pending' : 'idle';
  const holdsSlot = useTourGate((state) => pickSlotHolder(state) === tourId);

  useEffect(() => {
    useTourGate.getState().register(tourId, scope);
    return () => useTourGate.getState().unregister(tourId);
  }, [tourId, scope]);

  useEffect(() => {
    useTourGate.getState().setStatus(tourId, status);
  }, [tourId, scope, status]);

  return useBitkitTour({
    ...options,
    onClose: () => {
      setHasEnded(true);
      onClose?.();
    },
    onStatusChange: (details) => {
      if (details.status === 'started') {
        useTourGate.getState().markShown(tourId);
      }
      onStatusChange?.(details);
    },
    open: wantsToOpen && holdsSlot,
  });
};

export default useGatedBitkitTour;
